import { computed } from "mobx";
import { Router } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import LocalizationService from "services/localizationService";
import type { PropertyName } from "@frui.ts/helpers";
import { Modules } from "config";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import NotificationService from "services/notificationService";
import OptionsService from "services/optionsService";
import DeadLetterTopicMessagesSearchRequest from "entities/deadLetterTopicMessagesSearchRequest";
import type DeadLetterTopicMessageAction from "entities/deadLetterTopicMessageActionResponse";
import type DeadLetterTopicMessageActionsSearchRequest from "entities/deadLetterTopicMessageActionsSearchRequest";
import DeadLetterTopicActionType from "entities/deadLetterTopicActionType";
import DeadLetterTopicsRepository from "repositories/deadLetterTopicsRepository";
import ListViewModel from "viewModels/listViewModel";
import { SelectableListViewModel } from "viewModels/selectableListViewModel";

const navigationName = "dead-letter-topics-action-search";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
})
export default class DeadLetterTopicsActionSearchListViewModel extends ListViewModel<DeadLetterTopicMessageAction, ScreenBase> {
  readonly modelName = "deadLetterTopicsActionSearch";

  @computed
  get messageTypeOptions() {
    return this.optionsService.internalEnumOptions(DeadLetterTopicActionType);
  }

  @computed
  get self(): SelectableListViewModel<DeadLetterTopicsActionSearchListViewModel, DeadLetterTopicMessageAction> {
    return this as any;
  }

  @computed
  get name() {
    if (!this.isInitialized) {
      return this.translate("name");
    } else {
      return this.translate("nameCount").replace("%count%", this.currentPaging?.totalItems.toString() || "0");
    }
  }

  constructor(
    public repository: DeadLetterTopicsRepository,
    public securityService: SecurityService,
    public eventBus: EventBus,
    public localizationService: LocalizationService,
    public notificationService: NotificationService,
    private optionsService: OptionsService
  ) {
    super();
    this.navigationName = navigationName;
  }

  protected async fetchData() {
    const { limit, offset } = this.pagingFilter;
    return await this.repository.getAllActions({ offset, limit }, this.appliedFilter);
  }

  protected createFilter(): DeadLetterTopicMessagesSearchRequest {
    return new DeadLetterTopicMessagesSearchRequest();
  }

  translateFilterAttribute(attribute: PropertyName<DeadLetterTopicMessageActionsSearchRequest>, size?: number) {
    return this.localizationService.translateAttribute("deadLetterTopicMessageActionsSearchRequest", attribute, size);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `deadLetterTopicsActionSearch.${code}`, size);
  }
}
